import { Button, Card, CardContent, Stack, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { memo } from 'react';
import { FieldArrayWithId, useFieldArray, UseFormReturn } from 'react-hook-form';
import { InferType } from 'yup';

import { PlusIcon } from '@zeta-core/assets';
import { IClientBeneficiary } from '@zeta-types';
import CommonTableLayout from '../components/common-table-layout';
import { renderComponent } from '../components/render-component';
import { IGeneralFieldType } from '../components/render-fields';
import SimpleFieldsTable from '../components/simple-fields-table';
import { beneficiariesAndInsuranceValidationSchema, getBeneficiaryTableStructure, POLICIES_TABLE_STRUCTURE } from './form-helpers';

type beneficiariesAndInsuranceValidationSchemaType = InferType<typeof beneficiariesAndInsuranceValidationSchema>;
type useFormReturnType = UseFormReturn<beneficiariesAndInsuranceValidationSchemaType>;
type useFieldArrayReturnType = FieldArrayWithId<beneficiariesAndInsuranceValidationSchemaType, 'client.policies' | 'partner.policies' | 'joint.policies'>;

interface PolicyCardProps {
  policy: useFieldArrayReturnType;
  policyIndex: number;
  tab: 'client' | 'partner' | 'joint';
  methods: useFormReturnType;
  beneficiariesList: IClientBeneficiary[];
  isClient: boolean;
  insuranceTypeOptions: Array<any>;
}

const PolicyCard = memo(({ policyIndex, tab, methods, beneficiariesList, isClient, insuranceTypeOptions }: PolicyCardProps) => {
  const theme = useTheme();
  const policyDisplayIndex = policyIndex + 1;

  const fieldBeneficiariesArray = useFieldArray({
    name: `${tab}.policies.${policyIndex}.beneficiaries`,
    control: methods.control,
  });

  return (
    <Card sx={{ border: `1px solid ${theme.palette.grey[300]}`, boxShadow: 'none' }}>
      <CardContent sx={{ padding: 1.5 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle2" color="text.primary">
            Policy - {policyDisplayIndex}
          </Typography>

          <CommonTableLayout tableHead={POLICIES_TABLE_STRUCTURE(insuranceTypeOptions)}>
            <TableRow sx={{ borderBottom: `1px solid ${theme.palette.grey[500]} !important`, verticalAlign: 'top' }}>
              {POLICIES_TABLE_STRUCTURE(insuranceTypeOptions)?.map((tableContent: IGeneralFieldType) => (
                <TableCell key={tableContent.name}>{renderComponent(tableContent, `${tab}.policies.${policyIndex}.${tableContent.name}`)}</TableCell>
              ))}
            </TableRow>
          </CommonTableLayout>

          <SimpleFieldsTable
            fieldArray={fieldBeneficiariesArray.fields}
            currentTab={`${tab}.policies.${policyIndex}.beneficiaries`}
            onRemove={fieldBeneficiariesArray.remove}
            tableDetails={getBeneficiaryTableStructure(beneficiariesList)}
            isClient={isClient}
          />
          {isClient ? null : (
            <Stack flexDirection="row" marginTop={1}>
              <Button
                variant="text"
                color="primary"
                id="addBeneficiaryButton"
                size="small"
                startIcon={<PlusIcon />}
                onClick={() => {
                  fieldBeneficiariesArray.append({
                    name: '',
                    percentage: 0,
                    show: true,
                  });
                }}
              >
                Add Beneficiary
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
});

export default PolicyCard;
